import { newAmount } from '@orus.eu/amount'
import type { ParameterlessTranslationKey } from '@orus.eu/translations'

import type { InsuranceDeductible, PossibleInsuranceDeductible } from '../../../model/policy-item.ts'
import type { FrAupaPolicyItem, FrAupaPolicyItemsState } from '../fr-aupa-insurance.ts'

import { getFrAupaGeneralDeductible_v1_0_0, getFrAupaStandardGeneralDeductible_v1_0_0 } from './general-deductible.ts'

const noDeductible: PossibleInsuranceDeductible = { type: 'none' }

/** SDC and SDC_PLUS: no deductible, except ten points of AIPP deducted, which no amount type says. */
const permanentImpairmentDeductible: PossibleInsuranceDeductible = {
  type: 'text',
  value: 'Sans franchise, excepté pour l’AIPP : franchise de 10 %',
}

const assistanceAgreement: PossibleInsuranceDeductible = { type: 'text', value: 'Voir convention d’assistance' }

export type FrAupaDeductibleEntry = {
  additionalInfo: string | null
  /** The « mise en avant » column of the product sheet, when the guarantees table does not show the first line. */
  inGuaranteesTable?: string
  /**
   * The FRANCHISE column of the product sheet, one line each, where the panel cannot read them from the deductible:
   * named parts would make one row per part, a sentence the sheet cuts otherwise would stay whole.
   */
  lines?: readonly string[]
  /** The panel's title over the lines, when what the sheet lists there is not a deductible. */
  title?: ParameterlessTranslationKey
} & (
  | { deductible: PossibleInsuranceDeductible; select?: never }
  | { select: (state: FrAupaPolicyItemsState) => InsuranceDeductible; deductible?: never }
)

/**
 * Transcription of the "Possible deductibles" column of the GUARANTEES tab of the product sheet. The five damage
 * covers share the general deductible; every other one is fixed by the contract.
 */
export const frAupaDeductibleByPolicyItem_v1_0_0 = {
  civilLiability: { deductible: noDeductible, additionalInfo: null },
  operatingLiability: {
    inGuaranteesTable: '500 €',
    lines: ['Dommages corporels : 0 €', 'Autres dommages : 500 €'],
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        { label: 'Dommages corporels', deductible: { type: 'none' } },
        { label: 'Autres dommages', deductible: { type: 'fixedAmount', value: newAmount(500) } },
      ],
    },
    additionalInfo: null,
  },
  ecologicalDamage: { deductible: noDeductible, additionalInfo: null },
  criminalDefenseAndRecourse: {
    deductible: { type: 'interventionThresholdAmount', value: newAmount(300), baseText: 'de dommages' },
    additionalInfo: null,
  },
  driverProtection: {
    inGuaranteesTable: 'AIPP : 10 %',
    lines: ['0 €', 'Sauf AIPP : 10 %'],
    deductible: permanentImpairmentDeductible,
    additionalInfo: null,
  },
  glass: {
    inGuaranteesTable: 'Remplacement : entre 80 et 260 €. Réparation : 0 €',
    lines: ['Remplacement : 80 € + 10 % du montant des dommages (maxi 260 €)', 'Réparation : 0 €'],
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        {
          label: 'Remplacement',
          deductible: { type: 'text', value: '80 € + 10 % du montant des dommages, maximum 260 €' },
        },
        { label: 'Réparation', deductible: { type: 'none' } },
      ],
    },
    additionalInfo: null,
  },
  naturalDisasters: {
    lines: ['Franchise légale fixée par arrêté interministériel'],
    deductible: { type: 'text', value: 'Franchise légale' },
    additionalInfo: 'Fixée par arrêté interministériel',
  },
  technologicalDisasters: { deductible: noDeductible, additionalInfo: null },
  climateEvents: { select: getFrAupaStandardGeneralDeductible_v1_0_0, additionalInfo: null },
  terrorism: { select: getFrAupaStandardGeneralDeductible_v1_0_0, additionalInfo: null },
  fire: { select: getFrAupaGeneralDeductible_v1_0_0, additionalInfo: null },
  theft: { select: getFrAupaGeneralDeductible_v1_0_0, additionalInfo: null },
  allAccidentDamage: { select: getFrAupaGeneralDeductible_v1_0_0, additionalInfo: null },
  enhancedCompensation: {
    lines: ['Franchise de la garantie mobilisée'],
    deductible: { type: 'text', value: 'Franchise de la garantie dommages mise en jeu' },
    additionalInfo: 'Vol, incendie ou dommages tous accidents : l’option ne crée pas de franchise propre',
  },
  extendedDriver: {
    inGuaranteesTable: 'AIPP : 10 %',
    lines: ['0 €', 'Sauf AIPP : 10 %'],
    deductible: permanentImpairmentDeductible,
    additionalInfo: null,
  },
  vehicleEquipment: { deductible: noDeductible, additionalInfo: null },
  vehicleContents: {
    inGuaranteesTable: 'Vétusté déduite 1 % par mois',
    title: 'fr_aupa_deductible_title_depreciation',
    lines: [
      '1 % par mois à partir de l’achat du bien neuf, avec un maximum de 80 %',
      'Matériel professionnel de moins de 5 ans remplacé ou réparé : remboursé à neuf, sans vétusté',
    ],
    deductible: {
      type: 'text',
      value: 'Vétusté de 1 % par mois depuis l’achat des biens neufs, maximum 80 %',
    },
    additionalInfo: null,
  },
  reducedDeductible: { deductible: { type: 'not-applicable' }, additionalInfo: null },
  assistance: { deductible: assistanceAgreement, additionalInfo: null },
} as const satisfies Record<FrAupaPolicyItem, FrAupaDeductibleEntry>
